'use client';

import { useRef } from 'react';
import { motion, useSpring, useTransform, useMotionValue } from 'framer-motion';
import { EASE_OUT } from '@/lib/animations';

/* ── Color configs ── */
const THEME: Record<string, { bar: string; accent: string; accentBg: string; accentBorder: string; metricText: string; chartBar: string; chartBarActive: string }> = {
  amber:  { bar: 'bg-amber-500',  accent: 'text-amber-600',  accentBg: 'bg-amber-50',  accentBorder: 'border-amber-100',  metricText: 'text-amber-700',  chartBar: 'bg-amber-200/70',  chartBarActive: 'bg-amber-500' },
  blue:   { bar: 'bg-blue-500',   accent: 'text-blue-600',   accentBg: 'bg-blue-50',   accentBorder: 'border-blue-100',   metricText: 'text-blue-700',   chartBar: 'bg-blue-200/70',   chartBarActive: 'bg-blue-500' },
  orange: { bar: 'bg-orange-500', accent: 'text-orange-600', accentBg: 'bg-orange-50', accentBorder: 'border-orange-100', metricText: 'text-orange-700', chartBar: 'bg-orange-200/70', chartBarActive: 'bg-orange-500' },
  rose:   { bar: 'bg-rose-500',   accent: 'text-rose-600',   accentBg: 'bg-rose-50',   accentBorder: 'border-rose-100',   metricText: 'text-rose-700',   chartBar: 'bg-rose-200/70',   chartBarActive: 'bg-rose-500' },
  green:  { bar: 'bg-green-500',  accent: 'text-green-600',  accentBg: 'bg-green-50',  accentBorder: 'border-green-100',  metricText: 'text-green-700',  chartBar: 'bg-green-200/70',  chartBarActive: 'bg-green-500' },
  indigo: { bar: 'bg-indigo-500', accent: 'text-indigo-600', accentBg: 'bg-indigo-50', accentBorder: 'border-indigo-100', metricText: 'text-indigo-700', chartBar: 'bg-indigo-200/70', chartBarActive: 'bg-indigo-500' },
  teal:   { bar: 'bg-teal-500',   accent: 'text-teal-600',   accentBg: 'bg-teal-50',   accentBorder: 'border-teal-100',   metricText: 'text-teal-700',   chartBar: 'bg-teal-200/70',   chartBarActive: 'bg-teal-500' },
  pink:   { bar: 'bg-pink-500',   accent: 'text-pink-600',   accentBg: 'bg-pink-50',   accentBorder: 'border-pink-100',   metricText: 'text-pink-700',   chartBar: 'bg-pink-200/70',   chartBarActive: 'bg-pink-500' },
  violet: { bar: 'bg-violet-500', accent: 'text-violet-600', accentBg: 'bg-violet-50', accentBorder: 'border-violet-100', metricText: 'text-violet-700', chartBar: 'bg-violet-200/70', chartBarActive: 'bg-violet-500' },
  sky:    { bar: 'bg-sky-500',    accent: 'text-sky-600',    accentBg: 'bg-sky-50',    accentBorder: 'border-sky-100',    metricText: 'text-sky-700',    chartBar: 'bg-sky-200/70',    chartBarActive: 'bg-sky-500' },
  slate:  { bar: 'bg-slate-500',  accent: 'text-slate-600',  accentBg: 'bg-slate-50',  accentBorder: 'border-slate-100',  metricText: 'text-slate-700',  chartBar: 'bg-slate-200/70',  chartBarActive: 'bg-slate-500' },
};

/* ── Panel presets — each panelType gets different dashboard content ── */
const PRESETS: Record<string, { title: string; urlSlug: string; metrics: { val: string; label: string }[]; chartLabel: string; chartTrend: string; bars: number[]; statusItems: { label: string; dot: string }[] }> = {
  reservas: {
    title: 'Panel de Reservas',
    urlSlug: 'reservas',
    metrics: [
      { val: '127', label: 'Hoy' },
      { val: '94%', label: 'Ocupación' },
      { val: '€8.2k', label: 'Facturado' },
    ],
    chartLabel: 'Reservas / semana',
    chartTrend: '+28%',
    bars: [45, 60, 52, 70, 65, 80, 92],
    statusItems: [
      { label: 'Agenda sincronizada', dot: 'bg-green-500' },
      { label: '3 recordatorios hoy', dot: 'bg-sky-500' },
    ],
  },
  crm: {
    title: 'Panel CRM',
    urlSlug: 'crm',
    metrics: [
      { val: '342', label: 'Contactos' },
      { val: '28', label: 'Leads nuevos' },
      { val: '67%', label: 'Conversión' },
    ],
    chartLabel: 'Leads / semana',
    chartTrend: '+42%',
    bars: [30, 45, 55, 48, 62, 75, 88],
    statusItems: [
      { label: 'Pipeline activo', dot: 'bg-green-500' },
      { label: '5 follow-ups auto', dot: 'bg-sky-500' },
    ],
  },
  dashboard: {
    title: 'Panel Analytics',
    urlSlug: 'analytics',
    metrics: [
      { val: '€24.6k', label: 'Ingresos' },
      { val: '+18%', label: 'vs anterior' },
      { val: '92%', label: 'Retención' },
    ],
    chartLabel: 'Ingresos / semana',
    chartTrend: '+18%',
    bars: [55, 48, 62, 58, 72, 68, 85],
    statusItems: [
      { label: 'Datos en vivo', dot: 'bg-green-500' },
      { label: '2 alertas pendientes', dot: 'bg-amber-500' },
    ],
  },
  pagos: {
    title: 'Panel de Pagos',
    urlSlug: 'pagos',
    metrics: [
      { val: '€9.8k', label: 'Cobrado hoy' },
      { val: '99.2%', label: 'Tasa éxito' },
      { val: '14', label: 'Facturas auto' },
    ],
    chartLabel: 'Cobros / semana',
    chartTrend: '+34%',
    bars: [50, 65, 58, 72, 68, 82, 90],
    statusItems: [
      { label: 'Pagos activos', dot: 'bg-green-500' },
      { label: 'Stripe conectado', dot: 'bg-violet-500' },
    ],
  },
  automatizaciones: {
    title: 'Panel Automatizaciones',
    urlSlug: 'flows',
    metrics: [
      { val: '1.2k', label: 'Ejecuciones' },
      { val: '12', label: 'Flows activos' },
      { val: '99.8%', label: 'Uptime' },
    ],
    chartLabel: 'Ejecuciones / día',
    chartTrend: '+56%',
    bars: [35, 50, 65, 58, 78, 85, 95],
    statusItems: [
      { label: 'Flows corriendo', dot: 'bg-green-500' },
      { label: '0 errores hoy', dot: 'bg-emerald-500' },
    ],
  },
  web: {
    title: 'Panel Web',
    urlSlug: 'web',
    metrics: [
      { val: '3.4k', label: 'Visitas hoy' },
      { val: '4.2%', label: 'Conversión' },
      { val: '0.8s', label: 'Carga' },
    ],
    chartLabel: 'Visitas / semana',
    chartTrend: '+47%',
    bars: [40, 55, 48, 65, 72, 80, 92],
    statusItems: [
      { label: 'Web activa', dot: 'bg-green-500' },
      { label: '12 leads captados', dot: 'bg-blue-500' },
    ],
  },
};

interface SectorMockupProps {
  panelType: string;
  color: string;
  /** compact mode for embedding in smaller spaces */
  compact?: boolean;
  className?: string;
}

export default function SectorMockup({ panelType, color, compact = false, className = '' }: SectorMockupProps) {
  const t = THEME[color] || THEME.blue;
  const preset = PRESETS[panelType] || PRESETS.dashboard;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [3, -3]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-6, 6]), { stiffness: 150, damping: 20 });

  // Throttle mousemove to reduce INP — only update every ~32ms (≈30fps)
  const lastMove = useRef(0);
  const handleMouseMove = (e: any) => {
    const now = performance.now();
    if (now - lastMove.current < 32) return;
    lastMove.current = now;
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleMouseLeave = () => { x.set(0); y.set(0); };

  return (
    <div
      className={`relative w-full ${compact ? 'max-w-[380px]' : 'max-w-[460px]'} mx-auto ${className}`}
      style={{ perspective: '1200px' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        initial={{ opacity: 0, y: 20, rotateX: 4, rotateY: -4 }}
        animate={{ opacity: 1, y: 0, rotateX: 2, rotateY: -3 }}
        transition={{ duration: 1, ease: EASE_OUT, delay: 0.2 }}
        className="rounded-2xl overflow-hidden shadow-[0_24px_60px_rgba(99,102,241,0.12),0_6px_20px_rgba(15,23,42,0.08)]"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2.5 bg-white/90 border-b border-slate-200/60 backdrop-blur-xl">
          <span className="w-2 h-2 rounded-full bg-red-400/70" />
          <span className="w-2 h-2 rounded-full bg-amber-400/70" />
          <span className="w-2 h-2 rounded-full bg-green-400/70" />
          <div className="ml-2 flex-1 h-4 rounded-md bg-slate-100 border border-slate-200/60 flex items-center px-2 gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-[8px] text-slate-400 font-mono">innovatheratech.com/{preset.urlSlug}</span>
          </div>
        </div>

        {/* Dashboard content */}
        <div className={`bg-white/88 backdrop-blur-xl ${compact ? 'p-3.5 space-y-2' : 'p-4 space-y-2.5'}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-0.5">
            <span className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold text-slate-700`}>{preset.title}</span>
            <span className={`text-[8px] font-mono ${t.accent} ${t.accentBg} border ${t.accentBorder} px-1.5 py-0.5 rounded-full`}>● En vivo</span>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-1.5">
            {preset.metrics.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                className={`${t.accentBg} border ${t.accentBorder} rounded-lg ${compact ? 'p-2' : 'p-2.5'} text-center`}
              >
                <div className={`${compact ? 'text-xs' : 'text-sm'} font-bold ${t.metricText} leading-none`}>{m.val}</div>
                <div className={`text-[8px] ${t.accent} opacity-70 mt-0.5 font-medium`}>{m.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Chart */}
          <div className="bg-slate-50/80 border border-slate-100 rounded-lg p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[8px] font-semibold text-slate-600">{preset.chartLabel}</span>
              <span className={`text-[8px] ${t.accent} font-medium`}>{preset.chartTrend}</span>
            </div>
            <div className={`flex items-end gap-0.5 ${compact ? 'h-7' : 'h-9'}`}>
              {preset.bars.map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.6 + i * 0.05, duration: 0.4, ease: 'easeOut' as const }}
                  style={{ height: `${h}%`, transformOrigin: 'bottom', flex: 1 }}
                  className={`rounded-sm ${i === preset.bars.length - 1 ? t.chartBarActive : t.chartBar}`}
                />
              ))}
            </div>
            <div className="flex justify-between mt-0.5">
              {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
                <span key={d} className="text-[7px] text-slate-400 flex-1 text-center">{d}</span>
              ))}
            </div>
          </div>

          {/* Status row */}
          <div className="flex gap-1.5">
            {preset.statusItems.map((item, i) => (
              <div key={i} className="flex-1 flex items-center gap-1 bg-slate-50/80 border border-slate-100 rounded-lg px-2 py-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${item.dot} ${i === 0 ? 'animate-pulse' : ''}`} />
                <span className="text-[8px] text-slate-600 font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
